import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SectionEditor from '@/components/SectionEditor';
import InteractiveMap from '@/components/InteractiveMap';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { applyStyle } from '@/utils/styleHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({
    hero: { title: 'Contact Us', subtitle: "We'd love to hear from you. Let's discuss your packaging needs." },
    info: { address: 'SFNO 332/1B, Sri Visalakshi Nagar\nMettukuppam Main Road, Vanagaram\nChennai 600 095', phone: '+91-7299808164', email: 'info@challengepack.in' },
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pages/contact`);
        if (r.data?.content) setContent(prev => ({ ...prev, ...r.data.content }));
      } catch (error) { console.error('Failed to load contact content:', error); }
    })();
  }, []);

  const updateSection = (key) => (data) => setContent(prev => ({ ...prev, [key]: data }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! Your message has been sent successfully.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: 'Visit Us', details: ['SFNO 332/1B, Sri Visalakshi Nagar', 'Mettukuppam Main Road, Vanagaram', 'Chennai 600 095'] },
    { icon: Phone, title: 'Call Us', details: ['+91-7299808164', 'Mon-Fri: 9AM - 5:30PM'] },
    { icon: Mail, title: 'Email Us', details: ['info@challengepack.in'] },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      {/* Hero */}
      <section className="relative py-24 bg-card group">
        <div className="subtle-grid absolute inset-0 opacity-30"></div>
        <SectionEditor user={user} sectionTitle="Hero" fields={[
          { key: 'title', label: 'Title', type: 'title', styled: true },
          { key: 'subtitle', label: 'Subtitle', type: 'text', styled: true },
        ]} content={content.hero} pageName="contact" sectionKey="hero" onSave={updateSection('hero')} />
        <div className="text-center px-4 relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4 tracking-tighter" style={applyStyle(content.hero?.titleStyle)} data-testid="contact-title">{content.hero?.title || 'Contact Us'}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto" style={applyStyle(content.hero?.subtitleStyle)}>{content.hero?.subtitle || "Get in touch with our team. We're here to help with your packaging needs."}</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 group relative">
        <SectionEditor user={user} sectionTitle="Contact Info" fields={[
          { key: 'address', label: 'Address', type: 'text', multiline: true },
          { key: 'phone', label: 'Phone', type: 'text' },
          { key: 'email', label: 'Email', type: 'text' },
        ]} content={content.info} pageName="contact" sectionKey="info" onSave={updateSection('info')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-foreground mb-6 tracking-tight">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-foreground">Full Name *</Label>
                  <Input id="name" type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-card border-border/40 text-foreground" data-testid="contact-name-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-foreground">Email *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="bg-card border-border/40 text-foreground" data-testid="contact-email-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-foreground">Phone Number</Label>
                  <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-card border-border/40 text-foreground" data-testid="contact-phone-input" />
                </div>
                <div>
                  <Label htmlFor="company" className="text-foreground">Company Name</Label>
                  <Input id="company" type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="bg-card border-border/40 text-foreground" data-testid="contact-company-input" />
                </div>
                <div>
                  <Label htmlFor="message" className="text-foreground">Message *</Label>
                  <Textarea id="message" rows={5} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required className="bg-card border-border/40 text-foreground" data-testid="contact-message-input" />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg" disabled={loading} data-testid="contact-submit-button">
                  <Send className="w-5 h-5 mr-2" />
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-foreground mb-6 tracking-tight">Get In Touch</h2>
              <p className="text-muted-foreground mb-8">Have questions? We'd love to hear from you.</p>
              <div className="space-y-8">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <div key={`contact-${index}`} className="flex items-start" data-testid={`contact-info-${index}`}>
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">{info.title}</h3>
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-muted-foreground text-sm">{detail}</p>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Business Hours */}
              <div className="mt-12 bg-card p-6 rounded-xl border border-border/40">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-primary mr-3" />
                  <h3 className="text-lg font-semibold text-foreground">Business Hours</h3>
                </div>
                <div className="space-y-2 text-muted-foreground text-sm">
                  <p className="flex justify-between"><span>Monday - Friday:</span><span className="text-foreground/80 font-medium">9:00 AM - 5:30 PM</span></p>
                  <p className="flex justify-between"><span>Saturday:</span><span className="text-foreground/80 font-medium">9:00 AM - 4:00 PM</span></p>
                  <p className="flex justify-between"><span>Sunday:</span><span className="text-foreground/80 font-medium">Closed</span></p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <InteractiveMap />

      <Footer user={user} setUser={setUser} />
    </div>
  );
}
